/**
 * Dier bewerken (sessie 73 blok 2): van kaart naar formulier en van
 * formulier naar de body van PATCH en PUT allergieen. Alleen wat
 * veranderd is gaat mee; een leeg gemaakt veld gaat in `wissen`.
 */
import type { DierAllergieRij } from "../../api";
import type { DierWijziging, KaartDier } from "../types";

// ── Vaste lijsten ────────────────────────────────────────────────────

export const ALLERGENEN = [
  "kip",
  "rund",
  "lam",
  "varken",
  "vis",
  "zalm",
  "ei",
  "zuivel",
  "tarwe",
  "mais",
  "soja",
  "gluten",
  // Sessie 9P: eigen tags, los van kip (kipallergie-nuance 03-11).
  "kalkoen",
  "eend",
] as const;

/** Stand van één allergeen in het formulier; "allergie" alleen als hij al zo op de kaart staat. */
export type VermijdStand = "wens" | "intolerantie" | "allergie" | "allergie_dierenarts";

/** Conditie op de 9-schaal als woord (4 en 5 ideaal). */
export function conditieWoord(score: number): string {
  if (score <= 3) return "te mager";
  if (score <= 5) return "ideaal";
  if (score <= 7) return "te zwaar";
  return "veel te zwaar";
}

// ── Formulier naar body ──────────────────────────────────────────────

export interface DierInvoer {
  naam: string;
  ras: string;
  rasSleutel: string | null;
  geslacht: "mannelijk" | "vrouwelijk" | null;
  castratie: boolean | null;
  geboortedatum: string;
  geschat: boolean;
  gewicht: string;
  bcs: number | null;
  beweging: number | null;
  vermijden: Record<string, VermijdStand>;
}

function geslachtUit(geslacht: string | null): "mannelijk" | "vrouwelijk" | null {
  if (!geslacht) return null;
  const g = geslacht.trim().toLowerCase();
  if (["m", "man", "mannelijk", "male", "reu", "kater"].includes(g)) return "mannelijk";
  if (["v", "vrouw", "vrouwelijk", "female", "teef", "poes"].includes(g)) return "vrouwelijk";
  return null;
}

function vermijdUit(dier: KaartDier): Record<string, VermijdStand> {
  const uit: Record<string, VermijdStand> = {};
  for (const v of dier.vermijden) {
    uit[v.allergeen] =
      v.stand === "allergie"
        ? v.vastgesteld_dierenarts
          ? "allergie_dierenarts"
          : "allergie"
        : v.stand;
  }
  return uit;
}

export function invoerUit(dier: KaartDier): DierInvoer {
  return {
    naam: dier.naam,
    ras: dier.ras ?? "",
    rasSleutel: dier.ras_sleutel,
    geslacht: geslachtUit(dier.geslacht),
    castratie: dier.castratie,
    geboortedatum: dier.geboortedatum ? dier.geboortedatum.slice(0, 10) : "",
    geschat: dier.geboortedatum_geschat,
    gewicht: dier.gewicht_kg !== null ? String(dier.gewicht_kg).replace(".", ",") : "",
    bcs: dier.bcs_9,
    beweging: dier.beweging_uren_per_dag,
    vermijden: vermijdUit(dier),
  };
}

/** Gewicht uit de invoer ("32,5"); `undefined` als het geen getal is. */
export function leesGewicht(tekst: string): number | null | undefined {
  const schoon = tekst.trim().replace(",", ".");
  if (!schoon) return null;
  const n = Number(schoon);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

/**
 * De PATCH-body: alleen wat veranderd is, leeg gemaakt in `wissen`.
 * `null` als er niets veranderd is.
 */
export function bouwWijziging(dier: KaartDier, invoer: DierInvoer): DierWijziging | null {
  const oud = invoerUit(dier);
  const body: DierWijziging = {};
  const wissen: string[] = [];

  const naam = invoer.naam.trim();
  if (naam && naam !== oud.naam) body.naam = naam;

  const ras = invoer.ras.trim();
  if (ras !== oud.ras.trim() || invoer.rasSleutel !== oud.rasSleutel) {
    if (!ras) wissen.push("ras");
    else {
      body.ras = ras;
      // Uit de lijst gekozen: de sleutel gaat mee. Vrije tekst: alleen het ras.
      if (invoer.rasSleutel) body.ras_sleutel = invoer.rasSleutel;
    }
  }

  if (invoer.geslacht !== oud.geslacht) {
    if (invoer.geslacht === null) wissen.push("geslacht");
    else body.geslacht = invoer.geslacht;
  }

  if (invoer.castratie !== oud.castratie) {
    if (invoer.castratie === null) wissen.push("castratie");
    else body.castratie = invoer.castratie;
  }

  if (invoer.geboortedatum !== oud.geboortedatum) {
    if (!invoer.geboortedatum) wissen.push("geboortedatum");
    else body.geboortedatum = invoer.geboortedatum;
  }
  if (invoer.geboortedatum && (invoer.geschat !== oud.geschat || body.geboortedatum !== undefined)) {
    body.geboortedatum_geschat = invoer.geschat;
  }

  const gewicht = leesGewicht(invoer.gewicht);
  const oudGewicht = dier.gewicht_kg;
  if (gewicht !== undefined && gewicht !== oudGewicht) {
    if (gewicht === null) wissen.push("gewicht_kg");
    else body.gewicht_kg = gewicht;
  }

  if (invoer.bcs !== oud.bcs) {
    if (invoer.bcs === null) wissen.push("bcs_9");
    else body.bcs_9 = invoer.bcs;
  }

  if (invoer.beweging !== oud.beweging) {
    if (invoer.beweging === null) wissen.push("beweging_uren_per_dag");
    else body.beweging_uren_per_dag = invoer.beweging;
  }

  if (wissen.length) body.wissen = wissen;
  if (Object.keys(body).length === 0) return null;
  body.bron = "handmatig";
  return body;
}

function rij(allergeen: string, stand: VermijdStand): DierAllergieRij {
  if (stand === "wens") return { allergeen, niveau: "voorkeur", vastgesteld_door_dierenarts: false };
  if (stand === "intolerantie") {
    return { allergeen, niveau: "intolerantie", vastgesteld_door_dierenarts: false };
  }
  return {
    allergeen,
    niveau: "allergie",
    vastgesteld_door_dierenarts: stand === "allergie_dierenarts",
  };
}

/** De PUT-rijen voor vermijden, of `null` als er niets veranderd is. */
export function bouwAllergieen(dier: KaartDier, invoer: DierInvoer): DierAllergieRij[] | null {
  const oud = vermijdUit(dier);
  const vorm = (m: Record<string, VermijdStand>) =>
    Object.keys(m)
      .sort()
      .map((k) => `${k}=${m[k]}`)
      .join("|");
  if (vorm(oud) === vorm(invoer.vermijden)) return null;
  // Vervangt de hele lijst; een bestaande toelichting gaat mee terug.
  const toelichting = new Map(dier.vermijden.map((v) => [v.allergeen, v.toelichting]));
  return Object.keys(invoer.vermijden)
    .sort()
    .map((k) => {
      const uit = rij(k, invoer.vermijden[k]);
      const t = toelichting.get(k);
      return t ? { ...uit, toelichting: t } : uit;
    });
}
